import { Component, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { BusinessesList } from "../../components/businesses-list/businesses-list";
import { Router } from '@angular/router';
import { Routes } from 'src/app/core/routes/routes';
import { BusinessFilter } from "../../components/business-filter/business-filter";
import { PaginatorState } from 'primeng/paginator';
import { BusinessApi } from '@features/business/services/business-api';
import { DataEndpoint } from '@core/models/general.model';
import { BusinessFilterModel, BusinessGroupedModel, BusinessListModel, BusinessPaginatedModel, BusinessPaginatedRequestModel } from '@features/business/models/business.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmationDialog } from "@shared/components/confirmation-dialog/confirmation-dialog";
import { BlockUI } from "primeng/blockui";
import { ProgressSpinner } from "primeng/progressspinner";
import { NotHasRolePipe } from '@shared/pipes/not-has-role.pipe';
import { UserRole } from '@core/auth/services/auth-service';

@Component({
  selector: 'app-businesses',
  imports: [
    ButtonModule,
    BusinessesList,
    BusinessFilter,
    ToastModule,
    ConfirmationDialog,
    BlockUI,
    ProgressSpinner,
    NotHasRolePipe
],
  templateUrl: './businesses.html',
  styleUrl: './businesses.scss',
  providers: [MessageService, ConfirmationService]
})
export class Businesses {
  private router = inject(Router);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private businessApi = inject(BusinessApi);

  UserRole = UserRole;
  data: BusinessListModel[] = [];
  filteredData: BusinessListModel[] = [];
  businessGroupOptions: BusinessGroupedModel[] = [];
  isLoading: boolean = false;
  sortBy: string | null = null;
  sortOrder: 'asc' | 'desc' | null = null;
  currentFilters = signal<BusinessFilterModel>({});

  // Estado de paginación
  paginationState = signal<PaginatorState>({
    first: 0,
    rows: 10,
    page: 0
  });
  totalRecords: number = 0;

  // Variables para gestionar activar/inactivar empresa/grupo
  showStatusChangeDialog: boolean = false;
  selectedBusiness: BusinessListModel | null = null;
  changeStatusMessage: string = '';
  changeStatusButton: string = '';

  ngOnInit() {
    this.loadData();
    this.getBusinessGroupList();
  }

  loadData() {
    this.isLoading = true;

    const request: BusinessPaginatedRequestModel = {
      pageNumber: (this.paginationState().page ?? 0) + 1,
      pageSize: this.paginationState().rows,
      searchTerm: this.currentFilters().searchText,
      parentIds: this.currentFilters().selectedBusinessGroups,
      sortBy: this.sortBy ? this.columnKeyMap[this.sortBy] : undefined,
      sortOrder: this.sortOrder ?? undefined
    }

    this.businessApi.getBusinessList(request).subscribe({
      next: (response: DataEndpoint<BusinessPaginatedModel>) => {
        if (response.Status === 200) {
          this.data = response.Data?.records ?? [];
          this.filteredData = response.Data?.records ?? [];
          this.totalRecords = response.Data?.pagination.totalCount ?? 0;
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    })
  }

  onPageChange(state: PaginatorState) {
    this.paginationState.set(state);
    this.loadData();
  }

  getBusinessGroupList() {
    this.isLoading = true;

    this.businessApi.getBusinessGrouped().subscribe({
      next: (response: DataEndpoint<BusinessGroupedModel[]>) => {
        if (response.Status === 200) {
          this.businessGroupOptions = response.Data ?? [];
          this.isLoading = false;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    })
  }

  handleChangeBusinessStatus(business: BusinessListModel) {
    this.selectedBusiness = business;

    // Si la empresa va a ser desactivada
    if (business.Status === 'ENABLED') {
      this.changeStatusMessage = `¿Está seguro de que desea desactivar la empresa ${business.CompanyName}?`;
      this.changeStatusButton = 'Desactivar';
    }
    // Si la empresa va a ser activada
    else {
      this.changeStatusMessage = `¿Está seguro de que desea activar la empresa ${business.CompanyName}?`;
      this.changeStatusButton = 'Activar';
    }

    this.showStatusChangeDialog = true;
  }

  handleChangeBusinessGroupStatus(business: BusinessListModel) {
    this.selectedBusiness = business;

    // Si el grupo va a ser desactivado
    if (business.Status === 'ENABLED') {
      this.changeStatusMessage = `¿Está seguro de que desea desactivar el grupo ${business.GroupName}?`;
      this.changeStatusButton = 'Desactivar';
    }
    // Si el grupo va a ser activado
    else {
      this.changeStatusMessage = `¿Está seguro de que desea activar el grupo ${business.GroupName}?`;
      this.changeStatusButton = 'Activar';
    }

    this.showStatusChangeDialog = true;
  }

  confirmStatusChange() {
    if (this.selectedBusiness) {
      this.changeBusinessStatus(this.selectedBusiness.Id);
    }
    this.showStatusChangeDialog = false;
    this.selectedBusiness = null;
  }

  cancelStatusChange() {
    this.showStatusChangeDialog = false;
    this.selectedBusiness = null;
  }

  changeBusinessStatus(businessId: string) {
    if (businessId) {
      this.isLoading = true;

      this.businessApi.updateBusinessStatus(businessId).subscribe({
        next: (response: DataEndpoint<any>) => {
          if (response.Success) {
            this.messageService.add({
              severity: 'success',
              summary: 'Acción exitosa',
              detail: 'El estado ha sido actualizado satisfactoriamente.'
            });
            this.loadData();
          }
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.error?.Message ||
              'Ha ocurrido un error cambiando el estado. Por favor, comuníquese con soporte.'
          });
        }
      })
    }
    else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ha ocurrido un error cambiando el estado. Por favor, comuníquese con soporte.'
      });
    }
  }

  onCreateBusinessGroup() {
    this.router.navigate([Routes.businessesAddGroupPage])
  }

  onCreateBusiness() {
    this.router.navigate([Routes.businessesAddPage])
  }

  onFilterChange(filters: BusinessFilterModel) {
    this.currentFilters.set(filters);

    // Resetear a la primera página al filtrar
    this.paginationState.set({
      first: 0,
      rows: this.paginationState().rows,
      page: 0
    });

    this.loadData();
  }

  onClearFilters(): void {
    this.currentFilters.set({});

    // Resetear a la primera página al limpiar
    this.paginationState.set({
      first: 0,
      rows: this.paginationState().rows,
      page: 0
    });

    this.loadData();
  }

  // Configura las columnas con sort
  private columnKeyMap: Record<string, string> = {
    GroupName: 'GroupName',
    CompanyName: 'CompanyName',
    Sectors: 'Sectors',
    ContactName: 'ContactName',
    Identification: 'Identification',
    CreatedBy: 'CreatedBy',
    CreatedAt: 'CreatedAt',
    UpdatedAt: 'UpdatedAt',
    UpdatedBy: 'UpdatedBy'
  };

  // Toggle de estados: null -> asc -> desc -> null
  toggleSort(columnKey: string) {
    if (!this.columnKeyMap[columnKey]) return;

    if (this.sortBy !== columnKey) {
      // Nueva columna -> iniciar asc
      this.sortBy = columnKey;
      this.sortOrder = 'asc';
    } else {
      // Misma columna -> avanzar el ciclo
      if (this.sortOrder === 'asc') this.sortOrder = 'desc';
      else if (this.sortOrder === 'desc') {
        // Quitar sort
        this.sortBy = null;
        this.sortOrder = null;
      } else {
        this.sortOrder = 'asc';
      }
    }

    // Resetear la paginación al aplicar un nuevo orden
    this.paginationState().page = 0;
    this.loadData();
  }
}
